import Icon from '../Utilites/Icon';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { storageUrl } from '../Core/Config';
import { preventMediaAction } from '../Utilites/MediaProtection';
import { mediaTranslations } from '../Core/MediaTranslations';
import './VideoLightbox.css';

const formatTime = (time) => {
  const seconds = Math.max(0, Math.floor(time || 0));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
};

const VideoLightbox = ({ video }) => {
  const { t } = useTranslation();
  const label = (key) => t(key, { ns: 'media', defaultValue: mediaTranslations.en[key] });
  const [isOpen, setIsOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [position, setPosition] = useState(0);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [error, setError] = useState(false);
  const videoRef = useRef(null);
  const openerRef = useRef(null);
  const dialogRef = useRef(null);
  const playButtonRef = useRef(null);

  const play = () => {
    setError(false);
    const pending = videoRef.current?.play();
    pending?.catch((reason) => {
      // Closing the player can interrupt a pending play request.
      if (reason.name !== 'AbortError') setError(true);
    });
  };
  const close = useCallback(() => {
    videoRef.current?.pause();
    setIsOpen(false);
    setExpanded(false);
    openerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    playButtonRef.current?.focus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Safari does not necessarily focus a button when it is clicked.
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, close]);

  useEffect(() => {
    const player = videoRef.current;
    return () => { player?.pause(); };
  }, [video?.path]);

  const handleDialogKey = (event) => {
    if (event.key === 'Tab') {
      const controls = Array.from(dialogRef.current.querySelectorAll('button, input, select'))
        .filter(control => !control.disabled);
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  };

  if (!video?.path) {
    return <h3 className="text-red text-center pb-6 mx-auto">{t('video-not-found')}</h3>;
  }

  return (
    <div id="quiz-video">
      <span className="text-4xl">
        <Icon name="arrow_cool_down" />{t('start-here')}<Icon name="arrow_cool_down" />
      </span>
      <button type="button" ref={openerRef} aria-label={t('start-here')}
        onClick={() => { setIsOpen(true); play(); }}
        data-protected-media onContextMenu={preventMediaAction}
        className="btn-video text-primary border-primary">
        <video key={`preview-${video.path}`} width="300" height="180" muted playsInline preload="metadata" draggable={false}>
          <source src={`${storageUrl}${video.path}`} type={video.mime} />
        </video>
        <Icon name="play_circle" className="ico text-[5rem]" />
      </button>
      <span className="text-4xl mt-4">{t('now-you-can-play')}</span>

      <div className={`lightbox-overlay ${isOpen ? 'open' : ''}`} hidden={!isOpen} onClick={close}>
        <div ref={dialogRef} role="dialog" aria-modal="true" aria-label={label('title')}
          className={`lightbox-content protected-video ${expanded ? 'is-expanded' : ''}`}
          onClick={event => event.stopPropagation()} onKeyDown={handleDialogKey}
          data-protected-media onContextMenu={preventMediaAction}>
          <video key={video.path} ref={videoRef} width="890" height="500" playsInline
            preload="metadata" controls={false} controlsList="nodownload" draggable={false}
            onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => setPlaying(false)}
            onLoadedMetadata={event => {
              const length = event.currentTarget.duration;
              setDuration(Number.isFinite(length) ? length : 0);
              setPosition(0);
            }}
            onDurationChange={event => {
              const length = event.currentTarget.duration;
              setDuration(Number.isFinite(length) ? length : 0);
            }}
            onTimeUpdate={event => setPosition(event.currentTarget.currentTime)}
            onVolumeChange={event => {
              setVolume(event.currentTarget.volume);
              setMuted(event.currentTarget.muted);
            }} onError={() => setError(true)}>
            <source src={`${storageUrl}${video.path}`} type={video.mime} onError={() => setError(true)} />
            {t('video-not-supported')}
          </video>
          <div className="video-controls">
            <button type="button" ref={playButtonRef} onClick={() => playing ? videoRef.current.pause() : play()}>
              {label(playing ? 'pause' : 'play')}
            </button>
            <input className="video-seek" type="range" min="0" max={duration || 0} step="0.1"
              value={Math.min(position, duration)} disabled={!duration} aria-label={label('seek')}
              aria-valuetext={`${formatTime(position)} / ${formatTime(duration)}`}
              onChange={event => {
                videoRef.current.currentTime = Number(event.target.value);
                setPosition(Number(event.target.value));
              }} />
            <span className="video-time">{formatTime(position)} / {formatTime(duration)}</span>
            <button type="button" onClick={() => { videoRef.current.muted = !muted; }}>
              {label(muted ? 'unmute' : 'mute')}
            </button>
            <input className="video-volume" type="range" min="0" max="1" step="0.05"
              value={muted ? 0 : volume} aria-label={label('volume')}
              onChange={event => {
                videoRef.current.volume = Number(event.target.value);
                videoRef.current.muted = Number(event.target.value) === 0;
              }} />
            <select aria-label={label('speed')} defaultValue="1"
              onChange={event => { videoRef.current.playbackRate = Number(event.target.value); }}>
              {[0.5, 0.75, 1, 1.25, 1.5, 2].map(rate => <option key={rate} value={rate}>{rate}×</option>)}
            </select>
            <button type="button" aria-pressed={expanded} onClick={() => setExpanded(!expanded)}>
              {label(expanded ? 'reduce' : 'enlarge')}
            </button>
          </div>
          {error && <p className="video-error" role="alert">{label('error')}</p>}
          <button type="button" aria-label={label('close')} className="close-button btn btn-sm btn-red" onClick={close}>
            <Icon name="close" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default VideoLightbox;
